import { Stack } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash-es';
import React from 'react';
import { DATA_TEST } from '../enums';
import { calculateBadgeContent, getBadgeVariant } from '../MobileNavBar/MobileNavBar';
import { UtilityNames } from '../types';

import { OgAssist } from '../OgAssist';
import { ProfileSettingsOption } from './Components/ProfileSettingsOption';
import { TrayOption } from './Components/TrayOption';
import { useUtilityTrayContext } from './Context/UtilityTrayContext';
import { CreateNewActionButtonGroup } from './CreateNewAction/CreateNewActionButtonGroup';
import { SearchActionButton } from './SearchAction/SearchActionButton';

interface IUtilityTrayProps {
  condensedIcons?: boolean;
  collapseOptions?: boolean;
}
export const DesktopUtilityTray: React.FC<IUtilityTrayProps> = ({
  collapseOptions = false,
  condensedIcons = false
}) => {
  const {
    createNewActionOptions,
    searchOptions,
    helpOptions,
    reportsOptions,
    notificationsOptions,
    settingsOptions,
    profileSettingsOptions,
    customUtilityOptions,
    customProfileSlot,
    showOgAssist
  } = useUtilityTrayContext();

  const hasCustomProfileSlot = !isUndefined(customProfileSlot) && customProfileSlot !== null;
  const showProfileSettings =
    !hasCustomProfileSlot && !isUndefined(profileSettingsOptions) && !isEmpty(profileSettingsOptions);

  const utilityTrayRef = React.useRef<HTMLDivElement>(null);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      ml={1}
      gap={1}
      flexShrink={0}
      ref={utilityTrayRef}
      data-test={DATA_TEST.desktop_utility_tray_container}
    >
      {showOgAssist && <OgAssist />}
      <Stack direction="row" alignItems="center" gap={1} mr={1}>
        {searchOptions && (
          <SearchActionButton onClick={searchOptions.handleSearchIconClick} condensed={condensedIcons} />
        )}
        {createNewActionOptions && (
          <CreateNewActionButtonGroup actionItems={createNewActionOptions} condensed={condensedIcons} />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} data-test={DATA_TEST.desktop_utility_tray_options}>
        {/* Custom Utility Option */}
        {customUtilityOptions && !collapseOptions && <TrayOption {...customUtilityOptions} />}
        {/* Notifications */}
        {notificationsOptions && !collapseOptions && (
          <TrayOption utilityName={UtilityNames.notifications} {...notificationsOptions} />
        )}
        {/* Reports */}
        {reportsOptions && !collapseOptions && <TrayOption utilityName={UtilityNames.reports} {...reportsOptions} />}
        {/* Help */}
        {helpOptions && !collapseOptions && <TrayOption utilityName={UtilityNames.help} {...helpOptions} />}
        {/* Settings */}
        {settingsOptions && !collapseOptions && <TrayOption utilityName={UtilityNames.settings} {...settingsOptions} />}
        {/* Profile Settings */}
      </Stack>
      {hasCustomProfileSlot && customProfileSlot}
      {showProfileSettings && (
        <ProfileSettingsOption
          {...profileSettingsOptions}
          collapseOptions={collapseOptions}
          badgeContent={calculateBadgeContent(
            settingsOptions,
            notificationsOptions,
            helpOptions,
            reportsOptions,
            customUtilityOptions
          )}
          badgeVariant={getBadgeVariant(
            settingsOptions,
            notificationsOptions,
            helpOptions,
            reportsOptions,
            customUtilityOptions
          )}
        />
      )}
    </Stack>
  );
};
